import Api from "../api";
import {
  SEND_SMS,
  SEND_SMS_URL,
  SEND_CUSTOM_SMS_URL,
  SCHEDULE_SMS_URL,
  GET_SCHEDULED_SMS,
  RE_SCHEDULED_SMS,
  CANCEL_SCHEDULED_SMS,
  SMS_DETAILS,
  SMS_DETAILS_URL,
  SEND_CUSTOM_SMS,
  UPDATE_SMS_DETAILS,
} from "../lib/constant";
import { handleAPIDispatch, handleRejectCatch } from "../lib/utils";

// SMS type mapping from UI format to API format
const smsTypeToApiFormat = {
  'Transactional': 'TRANS',
  'Promotional': 'PROMO',
  'OTP': 'OTP',
  'TRANS': 'TRANS',
  'PROMO': 'PROMO',
};

// Transform plain Send-SMS UI params to API format (snake_case).
// Used by sendSms — uniform message text broadcast to a number[] list plus
// address-book selection (contact ids / group ids / all), resolved to
// mobiles server-side (SendSmsUIV1Request fields contact/groups/all).
function transformToApiParams(params) {
  const apiParams = {
    sender_id: params.senderId,
    message: params.message,
    sms_type: smsTypeToApiFormat[params.smsType] || params.smsType,
    number: params.number || [],
  };

  if (params.templateId) {
    apiParams.template_id = params.templateId;
  }
  if (params.all) {
    apiParams.all = true;
  }
  if (params.contact && params.contact.length) {
    apiParams.contact = params.contact;
  }
  if (params.groups && params.groups.length) {
    apiParams.groups = params.groups;
  }

  return apiParams;
}

// Transform Customize-SMS UI params to the /sms/send-custom request shape.
// The endpoint takes a {phone -> text} map; backend bills per-recipient
// segment count, not parts × count.
function transformToCustomApiParams(params) {
  const apiParams = {
    sender_id: params.senderId,
    sms_type: smsTypeToApiFormat[params.smsType] || params.smsType,
    custom_messages: params.customMessages || {},
  };

  if (params.templateId) {
    apiParams.template_id = params.templateId;
  }

  return apiParams;
}

export function sendSms(params) {
  const apiParams = transformToApiParams(params);
  const request = Api.post(SEND_SMS_URL, apiParams);
  return handleAPIDispatch(request, SEND_SMS);
}

export function updateSMSCount(data) {
  return (dispatch) => {
    dispatch({ type: UPDATE_SMS_DETAILS, payload: data });
  };
}

export function sendCustomizeSms(params) {
  const apiParams = transformToCustomApiParams(params);
  const request = Api.post(SEND_CUSTOM_SMS_URL, apiParams);
  return handleAPIDispatch(request, SEND_CUSTOM_SMS);
}

//Not in use
export function getScheduledSMS() {
  const request = Api.get(SCHEDULE_SMS_URL);
  return handleAPIDispatch(request, GET_SCHEDULED_SMS);
}

//Not in use
export function reScheduleSMS(id, params) {
  const request = Api.put(`${SCHEDULE_SMS_URL}/${id}`, params);
  return handleAPIDispatch(request, RE_SCHEDULED_SMS);
}

//Not in use
export function cancelScheduleSMS(id) {
  const request = Api.delete(`${SCHEDULE_SMS_URL}/${id}`);
  return handleAPIDispatch(request, CANCEL_SCHEDULED_SMS);
}

// SMS type mapping from API response to UI format
const smsTypeApiToUI = {
  'transactional': 'Transactional',
  'promotional': 'Promotional',
  'otp': 'OTP',
  'TRANS': 'Transactional',
  'PROMO': 'Promotional',
  'OTP': 'OTP',
  'Transactional': 'Transactional',
  'Promotional': 'Promotional',
};

// Transform smsCount from API format (lowercase) to UI format (capitalized)
export function transformSmsCountToUI(apiSmsCount) {
  if (!apiSmsCount) return {};
  const uiSmsCount = {};
  Object.keys(apiSmsCount).forEach(key => {
    const uiKey = smsTypeApiToUI[key] || key;
    uiSmsCount[uiKey] = parseInt(apiSmsCount[key]) || 0;
  });
  return uiSmsCount;
}

// Message type label mapping from wallet_name suffix
const messageTypeLabels = {
  'transactional': 'Transactional',
  'promotional': 'Promotional',
  'otp': 'OTP',
  'utility': 'Utility',
  'service': 'Service',
};

// Get SMS details — fetches wallets from billing API + SMS config (sender IDs, message types).
// This keeps SMSBalanceHeader, Dashboard, and all other consumers working unchanged.
//
// In-flight dedupe: getSMSDetails is wired into MenuBarDrawer, Sms, SmsHistory,
// TemplateForm, NewDashboard, and CustomizeSms — concurrent mounts on the same
// route used to fan out 2× wallets + 2× sms_config to the gateway. Hold a
// module-level promise so concurrent callers share one in-flight request.
let smsDetailsInFlight = null;

export function getSMSDetails() {
  return (dispatch) => {
    if (smsDetailsInFlight) {
      return smsDetailsInFlight;
    }
    const {GET_MY_WALLETS_URL, GET_SMS_CONFIG_URL} = require('../lib/constant');

    // Fetch wallets and SMS config in parallel
    const fetchPromise = Promise.all([
      Api.get(GET_MY_WALLETS_URL).catch(() => ({ data: {} })),
      Api.get(GET_SMS_CONFIG_URL).catch(() => ({ data: {} })),
    ])
      .then(([walletsRes, smsConfigRes]) => {
        // --- Wallets → smsCount (balances only) ---
        const wallets = walletsRes.data?.wallets || [];
        const smsCount = {};

        wallets.forEach(w => {
          const label = messageTypeLabels[w.messageType] || w.messageType || 'Unknown';
          const balance = parseInt(w.creditsBalance) || 0;
          smsCount[label] = (smsCount[label] || 0) + balance;
        });

        // --- SMS Config → senderIds, messageTypes ---
        const configData = smsConfigRes.data?.data || {};

        // The SMS Type picker is driven by sms_config messageTypes — the
        // source of truth for what the tenant MAY send. Wallets only carry
        // balances: a configured type with no wallet shows with balance 0
        // and Send stays gated by the existing insufficient-balance check
        // ("Not Sufficient amount to Send SMS"). Previously the list was
        // wallet-derived, which HID configured types (e.g. the default
        // transactional type) until a wallet existed for them.
        const rawMessageTypes = configData.messageTypes || configData.message_types || [];
        const smsType = [];
        rawMessageTypes.forEach(mt => {
          const key = (mt.messageType || mt.message_type || '').toLowerCase();
          const label = messageTypeLabels[key];
          if (label && !smsType.includes(label)) smsType.push(label);
        });
        // Back-compat: tenants whose sms_config has no message types yet
        // (pre-onboarding-rework rows) fall back to the wallet-derived list.
        if (!smsType.length) {
          Object.keys(smsCount).forEach(label => {
            if (!smsType.includes(label)) smsType.push(label);
          });
        }
        // Zero-fill balances for configured types with no wallet so the
        // header chips render "Transactional: 0" instead of omitting it.
        smsType.forEach(label => {
          if (!(label in smsCount)) smsCount[label] = 0;
        });
        const rawSenderIds = configData.senderIds || configData.sender_ids || [];
        // Store sender ID objects with headerType for UI to determine allowed SMS types
        const senderIdObjects = rawSenderIds
          .filter(s => (s.senderId || s.sender_id))
          .map(s => ({
            id: s.senderId || s.sender_id || '',
            headerType: s.headerType || s.header_type || '',
            isDefault: !!(s.isDefault || s.is_default),
          }));
        const senderIds = senderIdObjects.map(s => s.id);
        // Default header preference: explicit config value, then the
        // per-sender is_default flag, then first in the list.
        const flaggedDefault = senderIdObjects.find(s => s.isDefault);
        const defaultSenderId = configData.defaultSenderId || configData.default_sender_id
          || (flaggedDefault && flaggedDefault.id) || senderIds[0] || '';

        // Default type: sms_config's defaultMessageType when it maps to a
        // listed type, else first in the list.
        const configDefault = messageTypeLabels[
          (configData.defaultMessageType || configData.default_message_type || '').toLowerCase()
        ];
        const defaultSmsType = (configDefault && smsType.includes(configDefault))
          ? configDefault
          : (smsType[0] || 'Transactional');

        dispatch({
          type: SMS_DETAILS,
          payload: {
            success: true,
            data: {
              smsCount,
              scheduledSmsCount: {},
              smsType,
              smsTypeTaken: smsType,
              defaultSmsType,
              senderIds,
              senderIdObjects,
              defaultSenderId,
            }
          }
        });
      })
      .catch(() => {
        // Wallets / sms_config call failed — we don't actually know what
        // types the tenant has, so don't fabricate any. The UI will hide
        // the SMS Type select and Send button stays disabled.
        dispatch({
          type: SMS_DETAILS,
          payload: {
            success: true,
            data: {
              smsCount: {},
              scheduledSmsCount: {},
              smsType: [],
              smsTypeTaken: [],
              defaultSmsType: '',
              senderIds: [],
              defaultSenderId: '',
            }
          }
        });
      })
      .finally(() => {
        smsDetailsInFlight = null;
      });

    smsDetailsInFlight = fetchPromise;
    return fetchPromise;
  };
}
